import { isPlatformBrowser } from '@angular/common';
import { EventEmitterService } from '@core/services/event-emitter.service';
import { Component, Inject, OnInit, ChangeDetectorRef, PLATFORM_ID } from '@angular/core';
import { AnnouncementHttpService } from '@core/services/announcement-http.service';
import { CarouselModule, OwlOptions } from 'ngx-owl-carousel-o';
import { Router, RouterModule } from '@angular/router';
import { map } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { AppState } from '@store/reducers';
import { Observable } from 'rxjs/internal/Observable';
import { GameCategoryHttpService } from '@core/services/game-category-http.service';
import { GameLaunchWindowTargetService } from '@core/services/game-launch-target.service.ts';
import { Banner } from '@shared/models/banner.model';
import { svgIconList } from '@shared/svg-icon-list';
import { BannerHttpService } from '@core/services/banner-http.service';
import { IsLoggedIn } from '@core/entity/auth/state/auth.selectors';
import { CommonModule } from '@angular/common';
import { SafeHtmlPipe } from '@shared/pipes/safe-html.pipe';

@Component({
  selector: 'app-slider',
  imports: [CommonModule, SafeHtmlPipe, RouterModule, CarouselModule],
  templateUrl: './slider.component.html',
  styleUrls: ['./slider.component.scss'],
})
export class SliderComponent implements OnInit {

  visible = false;
  banners: any[] = [];
  bannerCount = this.banners.length;
  bannerPosition = 0;
  isSkeletonBanner = true;
  isLoggedIn$: Observable<boolean>;
  banners$ :Observable<Banner[]>;

  customOptions: OwlOptions = {
    loop: true,
    mouseDrag: false,
    touchDrag: true,
    pullDrag: false,
    dots: true,
    navSpeed: 1000,
    autoplaySpeed: 1000,
    autoplay: true,
    items: 1,
    nav: false,
    animateOut: 'animate__animated animate__slideOutDown',
    responsive: {
      0: {
        items: 1
      },
      350: {
        items: 1
      },
      600: {
        items: 1
      },
    }
  };
  svg: any = svgIconList;


  isBrowser: boolean;

  constructor(
    private store: Store<AppState>,
    private bannerHttpService: BannerHttpService,
    private announcementHttpService: AnnouncementHttpService,
    private eventEmitterService: EventEmitterService,
    private router: Router,
    private gameCategoryHttpService: GameCategoryHttpService,
    private cdr: ChangeDetectorRef,
    private gameLaunchWindowTargetService: GameLaunchWindowTargetService,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit() {
    this.isLoggedIn$ = this.store.pipe(select(IsLoggedIn));
    this.getBanner();

    this.eventEmitterService.resetBannerVar = this.eventEmitterService.resetBannerEmitter.subscribe(() => {
      this.isLoggedIn$ = this.store.pipe(select(IsLoggedIn));
      this.getBanner();
    });
  }

  getBanner(){
    // /banner resolves under apiBase (.../api/member/banner) which is excluded
    // from the SSR transfer cache, so if the server renders the slider the client
    // can't reuse it and re-fetches → "slider → loader → slider" flash (and a
    // hydration mismatch risk). Keep SSR at the skeleton; fetch on the client only.
    if (!this.isBrowser) return;
    this.isSkeletonBanner = true;
    this.bannerHttpService.getAllBanner().subscribe(res=>{
      this.banners = [];
      this.cdr.detectChanges();
      let ListBanners = res;

      this.isLoggedIn$.subscribe(res => {
        if (res) {
          this.banners = ListBanners.filter(banner => banner.images[0].image_desktop !== null && (banner.session == 1 || banner.session == 3));
        } else {
          this.banners = ListBanners.filter(banner => banner.images[0].image_desktop !== null && (banner.session == 1 || banner.session == 2));
        }

        // Reveal the carousel only once the first banner image is cached; the
        // <img> then paints immediately instead of downloading after render,
        // which otherwise collapses the slider to 0 height (skeleton → blank
        // → slider). Keep the skeleton up until then.
        const firstImage = this.banners[0]?.images?.[0]?.image_desktop;
        const reveal = () => { this.isSkeletonBanner = false; this.cdr.detectChanges(); };
        if (firstImage) {
          const preload = new Image();
          preload.onload = reveal;
          preload.onerror = reveal;
          preload.src = firstImage;
        } else {
          reveal();
        }
      });
    });
  }
  getOffset(el: any) {
    el = el.getBoundingClientRect();
    return {
      left: el.left + (this.isBrowser ? window.scrollX : 0),
      top: el.top + (this.isBrowser ? window.scrollY : 0),
      bottom: el.top + (this.isBrowser ? window.scrollY : 0)
    };
  }

  scroll() {
    if (!this.isBrowser) return;
    const scrollTo = document.getElementById('content');
    const topHight = this.getOffset(scrollTo).top;
    window.scrollTo(0, topHight);
  }

  onRedirect(link: string){
    if (!this.isBrowser || link === '') return;
    if (link.includes('external')) {
      this.router.navigate([]).then(() => { window.open(link, '_blank'); });
    } else {
      this.router.navigate([]).then(() => { window.open(link, '_self'); });
    }
  }

  onLaunchGame(data: string) {
    if (!this.isBrowser) return;
    // E.g data : Launch|EZ||481000, Launch|EZ|roulette|481000, Launch|EZ|roulette|481000|LC
    let datas = data.split('|');
    let gp_cat = datas[4] !== undefined ? datas[4] : 'LC';
    this.gameCategoryHttpService.getGames(gp_cat).pipe(
      map(res => res.find(x => x.game_provider_code === datas[1]))
    ).subscribe((gameProvider: any) => {
      localStorage.setItem('gameProviderCode', gameProvider.game_provider_code);
      localStorage.setItem('gameCategory', gp_cat);
      localStorage.setItem('isPlayTechClicked', (gameProvider.game_provider_code === "PT").toString());
      localStorage.setItem('gameProviderId', gameProvider.game_provider_id);
      datas[2] !== undefined ? localStorage.setItem('subCategoryCode', datas[2]) : localStorage.removeItem('subCategoryCode');
      datas[3] !== undefined ? localStorage.setItem('gameCode', datas[3]) : localStorage.removeItem('gameCode');
      localStorage.setItem('lobbyUrl', location.href);

      if (this.isBrowser) {
        this.router.navigate([]).then(() => { window.open('/gamelaunch', this.gameLaunchWindowTargetService.getTarget(gameProvider.game_provider_code)); });
      }
    })
  }
}
